import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import apiClient from '../../utils/api';
import {
  UniformDeliveryStatus,
  getUniformDeliveryStatusLabel,
} from '../../utils/uniformDeliveryStatus';

const UniformsContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: hidden;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.375rem;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const SearchSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? '#000000' : '#dee2e6'};
  background: ${props => props.active ? '#000000' : 'white'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: #000000;
    background: ${props => props.active ? '#000000' : '#f8f9fa'};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const UniformsList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem 0 1rem 0.5rem;
    border-radius: 12px;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    margin: 0;
    padding: 0;
    width: 100%;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  
  @media (max-width: 768px) {
    table-layout: fixed;
    min-width: 100%;
  }
`;

const Thead = styled.thead`
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const Th = styled.th`
  padding: 1rem 0.75rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    white-space: normal;
  }
`;

const NameTh = styled.th`
  padding: 1rem 0.75rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    white-space: normal;
    width: 18.18%;
  }
`;

const SizeTh = styled.th`
  padding: 1rem 0.75rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    white-space: normal;
    width: 31.82%;
  }
`;

const GenderTh = styled.th`
  padding: 1rem 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0;
    font-size: 0.75rem;
    width: 9.09%;
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 1rem 0.75rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    white-space: normal;
    word-break: break-word;
  }
`;

const SizeCell = styled.td`
  padding: 1rem 0.75rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: nowrap;
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    white-space: normal;
    word-break: break-word;
    width: 31.82%;
  }
`;

const GenderCell = styled.td`
  padding: 1rem 0.5rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: nowrap;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0 0.75rem 1.05rem;
    font-size: 0.75rem;
    width: 9.09%;
  }
`;

const NumberCell = styled.td`
  padding: 1rem 0.5rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: nowrap;
  text-align: center;
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0;
    font-size: 0.75rem;
    width: 9.09%;
  }
`;

const NicknameCell = styled.td`
  padding: 1rem 0.5rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: nowrap;
  cursor: pointer;
  min-width: 120px;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    white-space: normal;
    word-break: break-word;
    width: 31.82%;
  }
`;

const NumberTh = styled.th`
  padding: 1rem 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0;
    font-size: 0.75rem;
    width: 9.09%;
  }
`;

const NicknameTh = styled.th`
  padding: 1rem 0.5rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  min-width: 120px;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    width: 31.82%;
  }
`;

const NameCell = styled.td`
  padding: 1rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.8rem 0.75rem 0;
    font-size: 0.75rem;
    width: 18.18%;
  }
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const NameText = styled.span`
  white-space: nowrap;
`;

const RoleBadge = styled.span<{ role?: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => {
    if (props.role === '회장') return '#e7f5ff';
    if (props.role === '부회장') return '#fff3e0';
    if (props.role === '총무') return '#f3e5f5';
    if (props.role === '매니저') return '#e8f5e9';
    return '#f8f9fa';
  }};
  color: ${props => {
    if (props.role === '회장') return '#1971c2';
    if (props.role === '부회장') return '#e67700';
    if (props.role === '총무') return '#9c27b0';
    if (props.role === '매니저') return '#2e7d32';
    return '#495057';
  }};
  white-space: nowrap;
  display: inline-block;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }
`;

const NumberBadge = styled.span`
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  background: #000000;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.2rem 0.5rem;
  }
`;

const EmptyCell = styled.span`
  color: #adb5bd;
  font-style: italic;
  font-size: 0.8125rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
  display: block;
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #343a40;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #000000;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoBox = styled.div`
  background: #e7f5ff;
  border: 1px solid #339af0;
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.875rem;
  color: #1971c2;
  line-height: 1.6;
`;

const ActionTh = styled.th`
  padding: 1rem 0.75rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
    width: 22%;
  }
`;

const ActionCell = styled.td`
  padding: 1rem 0.5rem;
  text-align: center;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    width: 22%;
  }
`;

const RowActionButton = styled.button<{ $variant: 'new' | 'receive' | 'undo' }>`
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border: none;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease;
  white-space: nowrap;

  background: ${props =>
    props.$variant === 'new' ? '#212529' :
    props.$variant === 'receive' ? '#0d6efd' :
    'transparent'
  };
  color: ${props => props.$variant === 'undo' ? '#6c757d' : '#ffffff'};
  text-decoration: ${props => props.$variant === 'undo' ? 'underline' : 'none'};

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: 768px) {
    padding: 0.35rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const StatusBadge = styled.span<{ $status: UniformDeliveryStatus }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props =>
    props.$status === 'new' ? '#fff3cd' :
    props.$status === 'ready' ? '#cfe2ff' :
    '#d1e7dd'
  };
  color: ${props =>
    props.$status === 'new' ? '#856404' :
    props.$status === 'ready' ? '#084298' :
    '#0f5132'
  };
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const DialogBox = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const DialogMessage = styled.p`
  margin: 0 0 1.25rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
  white-space: pre-line;
`;

const DialogActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

type StatusFilter = 'all' | UniformDeliveryStatus;

interface Uniform {
  user_no: number;
  name: string;
  gender: string;
  election_status?: string;
  electionStatus: string;
  status: string;
  number: string;
  nickname: string;
  size: string;
  delivery_status: UniformDeliveryStatus;
  applied_at?: string | null;
  received_at?: string | null;
}

const AdminUniforms: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUniform, setEditingUniform] = useState<Uniform | null>(null);
  const selectedVersion = 2;
  const [sortBy, setSortBy] = useState<'number' | 'name'>('number');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [formData, setFormData] = useState({
    number: '',
    nickname: '',
    size: ''
  });
  const [uniforms, setUniforms] = useState<Uniform[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<{ type: 'confirm' | 'alert'; message: string } | null>(null);
  const dialogResolveRef = useRef<((value: boolean) => void) | null>(null);

  const showConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      dialogResolveRef.current = resolve;
      setDialog({ type: 'confirm', message });
    });
  }, []);

  const showAlert = useCallback((message: string): Promise<void> => {
    return new Promise((resolve) => {
      dialogResolveRef.current = () => resolve();
      setDialog({ type: 'alert', message });
    });
  }, []);

  const closeDialog = (result: boolean) => {
    setDialog(null);
    dialogResolveRef.current?.(result);
    dialogResolveRef.current = null;
  };

  const mapUniformFromApi = (raw: Record<string, unknown>): Uniform => ({
    user_no: raw.user_no as number,
    name: raw.name as string,
    gender: raw.gender as string,
    election_status: raw.election_status as string | undefined,
    electionStatus: (raw.election_status as string) || '비선출',
    status: raw.status as string,
    number: (raw.number as string) || '',
    nickname: (raw.nickname as string) || '',
    size: (raw.size as string) || '',
    delivery_status: (raw.delivery_status as UniformDeliveryStatus) || 'new',
    applied_at: (raw.applied_at as string) || null,
    received_at: (raw.received_at as string) || null,
  });

  const fetchUniforms = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/uniforms/applications', {
        params: { version: 2 },
      });
      if (response.data.success) {
        setUniforms((response.data.data || []).map(mapUniformFromApi));
      }
    } catch (error) {
      console.error('유니폼 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateDeliveryStatus = async (
    userNo: number,
    deliveryStatus: UniformDeliveryStatus
  ): Promise<boolean> => {
    try {
      const response = await apiClient.put(
        `/uniforms/${userNo}/${selectedVersion}/delivery-status`,
        { delivery_status: deliveryStatus }
      );
      if (response.data.success) {
        const updated = mapUniformFromApi(response.data.data);
        setUniforms(prev =>
          prev.map(item => (item.user_no === userNo ? updated : item))
        );
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('유니폼 수령 상태 변경 실패:', error);
      await showAlert(error.response?.data?.detail || '수령 상태 변경에 실패했습니다');
      return false;
    }
  };

  useEffect(() => {
    fetchUniforms();
  }, []);

  const filteredUniforms = uniforms
    .filter(uniform => {
      if (!uniform.name.includes(searchTerm)) return false;
      if (statusFilter === 'all') return true;
      return uniform.delivery_status === statusFilter;
    })
    .sort((a, b) => {
      if (sortBy === 'number') {
        const aNumber = a.number ? parseInt(a.number) || 0 : Infinity;
        const bNumber = b.number ? parseInt(b.number) || 0 : Infinity;
        return aNumber - bNumber;
      }
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name, 'ko');
      }
      return 0;
    });

  const stats = {
    total: uniforms.length,
    newCount: uniforms.filter(u => u.delivery_status === 'new').length,
    readyCount: uniforms.filter(u => u.delivery_status === 'ready').length,
    receivedCount: uniforms.filter(u => u.delivery_status === 'received').length,
  };

  const handleAcknowledgeNew = async (uniform: Uniform, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await showConfirm(`${uniform.name}님이 신규 신청하였습니다.\n\n확인 후 수령 단계로 넘어갑니다.`))) return;
    await updateDeliveryStatus(uniform.user_no, 'ready');
  };

  const handleMarkReceived = async (uniform: Uniform, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await showConfirm(`${uniform.name}님의 유니폼 수령을 완료 처리하시겠습니까?`))) return;
    await updateDeliveryStatus(uniform.user_no, 'received');
  };

  const handleUndoReceived = async (uniform: Uniform, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(await showConfirm(`${uniform.name}님의 수령 완료를 취소하고 수령 대기 상태로 되돌리시겠습니까?`))) return;
    await updateDeliveryStatus(uniform.user_no, 'ready');
  };

  const renderActionButtons = (uniform: Uniform) => {
    const deliveryStatus = uniform.delivery_status;

    if (deliveryStatus === 'new') {
      return (
        <RowActionButton $variant="new" onClick={(e) => handleAcknowledgeNew(uniform, e)}>
          신규
        </RowActionButton>
      );
    }

    if (deliveryStatus === 'ready') {
      return (
        <RowActionButton $variant="receive" onClick={(e) => handleMarkReceived(uniform, e)}>
          수령
        </RowActionButton>
      );
    }

    if (deliveryStatus === 'received') {
      if (statusFilter === 'received') {
        return (
          <RowActionButton $variant="undo" onClick={(e) => handleUndoReceived(uniform, e)}>
            되돌리기
          </RowActionButton>
        );
      }
      return null;
    }

    return null;
  };

  const handleUniformClick = (uniform: Uniform) => {
    setEditingUniform(uniform);
    setFormData({
      number: uniform.number || '',
      nickname: uniform.nickname || '',
      size: uniform.size || ''
    });
    setIsModalOpen(true);
  };

  // 배번 중복 체크 함수
  const isNumberAvailable = (number: string, currentUserNo: number, version: number): boolean => {
    if (!number) return true;

    const currentMember = uniforms.find(u => u.user_no === currentUserNo);
    if (!currentMember) return false;

    // 같은 버전의 유니폼에서 중복 체크
    const duplicates = uniforms.filter(u => {
      if (u.user_no === currentUserNo) return false;
      return u.number === number;
    });

    for (const dup of duplicates) {
      // 같은 성별이면 중복 불가
      if (dup.gender === currentMember.gender) {
        return false;
      }
      
      // 다른 성별인데 둘 다 선출이면 중복 불가
      if (dup.electionStatus === '선출' && currentMember.electionStatus === '선출') {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingUniform) return;

    // 배번 중복 체크
    if (formData.number && !isNumberAvailable(formData.number, editingUniform.user_no, selectedVersion)) {
      const duplicateInfo = uniforms.find(u => {
        if (u.user_no === editingUniform.user_no) return false;
        return u.number === formData.number &&
               (u.gender === editingUniform.gender || 
                (u.electionStatus === '선출' && editingUniform.electionStatus === '선출'));
      });
      
      if (duplicateInfo) {
        await showAlert(`배번 ${formData.number}번은 이미 ${duplicateInfo.name}(${duplicateInfo.gender}, ${duplicateInfo.electionStatus})이 사용 중입니다.`);
      } else {
        await showAlert('이미 사용 중인 배번입니다.');
      }
      return;
    }

    try {
      const response = await apiClient.put(`/uniforms/${editingUniform.user_no}/${selectedVersion}`, {
        number: formData.number || null,
        nickname: formData.nickname || null,
        size: formData.size || null
      });
      
      if (response.data.success) {
        await showAlert('유니폼 정보가 저장되었습니다');
        fetchUniforms();
        setIsModalOpen(false);
        setEditingUniform(null);
      }
    } catch (error: any) {
      console.error('유니폼 저장 실패:', error);
      await showAlert(error.response?.data?.detail || '유니폼 저장에 실패했습니다');
    }
  };

  const handleDelete = async () => {
    if (!editingUniform) return;
    
    if (await showConfirm('유니폼 정보를 삭제하시겠습니까?')) {
      try {
        const response = await apiClient.delete(`/uniforms/${editingUniform.user_no}/${selectedVersion}`);
        
        if (response.data.success) {
          await showAlert('유니폼 정보가 삭제되었습니다');
          fetchUniforms();
          setIsModalOpen(false);
          setEditingUniform(null);
        }
      } catch (error: any) {
        console.error('유니폼 삭제 실패:', error);
        await showAlert(error.response?.data?.detail || '유니폼 삭제에 실패했습니다');
      }
    }
  };

  const handleExcelDownload = () => {
    try {
      const headers = ['이름', '성별', '배번', '닉네임', '사이즈', '상태'];
      
      const csvData = filteredUniforms.map(uniform => [
          uniform.name,
          uniform.gender === '남자' ? '남' : uniform.gender === '여자' ? '여' : uniform.gender,
          uniform.number || '',
          uniform.nickname || '',
          uniform.size || '',
          getUniformDeliveryStatusLabel(uniform.delivery_status),
        ]);
      
      // CSV 문자열 생성
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => {
          // 셀 내용에 쉼표나 따옴표가 있으면 처리
          const cellStr = String(cell || '');
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');
      
      // BOM 추가 (한글 깨짐 방지)
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // 파일 다운로드
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `유니폼_관리_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      showAlert('엑셀 다운로드에 실패했습니다');
    }
  };

  return (
    <DashboardLayout title="유니폼 관리">
      <UniformsContainer>
        <Header>
          <Title>유니폼 관리</Title>
          <Button 
            variant="primary"
            onClick={handleExcelDownload}
          >
            엑셀 다운로드
          </Button>
        </Header>
        
        <StatsRow>
          <StatCard>
            <StatLabel>전체 신청</StatLabel>
            <StatValue>{stats.total}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>신규</StatLabel>
            <StatValue>{stats.newCount}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>수령대기</StatLabel>
            <StatValue>{stats.readyCount}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>수령완료</StatLabel>
            <StatValue>{stats.receivedCount}명</StatValue>
          </StatCard>
        </StatsRow>
        
        <SearchSection>
          <SearchRow>
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="이름으로 검색"
            />
            <Button 
              variant="primary"
              onClick={() => {}}
            >
              검색
            </Button>
          </SearchRow>
          <FilterRow>
            <FilterButton active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
              전체
            </FilterButton>
            <FilterButton active={statusFilter === 'new'} onClick={() => setStatusFilter('new')}>
              신규
            </FilterButton>
            <FilterButton active={statusFilter === 'ready'} onClick={() => setStatusFilter('ready')}>
              수령대기
            </FilterButton>
            <FilterButton active={statusFilter === 'received'} onClick={() => setStatusFilter('received')}>
              수령완료
            </FilterButton>
          </FilterRow>
          <FilterRow>
            <FilterButton 
              active={sortBy === 'number'}
              onClick={() => setSortBy('number')}
            >
              배번순
            </FilterButton>
            <FilterButton 
              active={sortBy === 'name'}
              onClick={() => setSortBy('name')}
            >
              이름순
            </FilterButton>
          </FilterRow>
        </SearchSection>
        
        <UniformsList>
          
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : filteredUniforms.length > 0 ? (
            <TableContainer>
              <Table>
                <Thead>
                  <tr>
                    <NameTh>이름</NameTh>
                    <GenderTh>성별</GenderTh>
                    <NumberTh>배번</NumberTh>
                    <NicknameTh>닉네임</NicknameTh>
                    <SizeTh>사이즈</SizeTh>
                    <ActionTh>상태</ActionTh>
                    <ActionTh>관리</ActionTh>
                  </tr>
                </Thead>
                <Tbody>
                  {filteredUniforms.map(uniform => {
                    const deliveryStatus = uniform.delivery_status;
                    return (
                    <Tr key={uniform.user_no}>
                      <NameCell onClick={() => handleUniformClick(uniform)}>
                        <NameContainer>
                          <NameText>{uniform.name}</NameText>
                          <RoleBadge role={uniform.status}>
                            {uniform.status}
                          </RoleBadge>
                        </NameContainer>
                      </NameCell>
                      <GenderCell>{uniform.gender === '남자' ? '남' : uniform.gender === '여자' ? '여' : uniform.gender}</GenderCell>
                      <NumberCell onClick={() => handleUniformClick(uniform)}>
                        {uniform.number ? (
                          <NumberBadge>{uniform.number}</NumberBadge>
                        ) : (
                          <EmptyCell>미등록</EmptyCell>
                        )}
                      </NumberCell>
                      <NicknameCell onClick={() => handleUniformClick(uniform)}>
                        {uniform.nickname || <EmptyCell>미등록</EmptyCell>}
                      </NicknameCell>
                      <SizeCell onClick={() => handleUniformClick(uniform)}>
                        {uniform.size || <EmptyCell>미등록</EmptyCell>}
                      </SizeCell>
                      <ActionCell>
                        <StatusBadge $status={deliveryStatus}>
                          {getUniformDeliveryStatusLabel(deliveryStatus)}
                        </StatusBadge>
                      </ActionCell>
                      <ActionCell>
                        {renderActionButtons(uniform)}
                      </ActionCell>
                    </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyState>
              {uniforms.length === 0
                ? '유니폼 신청한 회원이 없습니다.'
                : '검색 결과가 없습니다.'}
            </EmptyState>
          )}
        </UniformsList>
        
        {/* 유니폼 정보 수정 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingUniform?.name} - 유니폼 정보</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            {editingUniform && (
              <>
                <InfoBox>
                  <strong>배번 중복 규칙:</strong>
                  <br />
                  • 같은 성별은 배번 중복 불가
                  <br />
                  • 다른 성별은 중복 가능
                  <br />
                  • 단, 선출끼리는 성별 관계없이 중복 불가
                  <br />
                  <br />
                  <strong>현재 회원:</strong> {editingUniform.gender}, {editingUniform.electionStatus}
                </InfoBox>
                
                <ModalForm onSubmit={handleSubmit}>
                  <Input
                    label="배번"
                    type="text"
                    value={formData.number}
                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                    placeholder="예: 10"
                  />
                  
                  <Input
                    label="닉네임"
                    type="text"
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                    placeholder="예: 철수"
                  />
                  
                  <Input
                    label="사이즈"
                    type="text"
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    placeholder="예: S, M, L, XL"
                  />
                  
                  <ModalActions>
                    {(formData.number || formData.nickname || formData.size) && (
                      <Button
                        type="button"
                        variant="danger"
                        size="large"
                        fullWidth
                        onClick={handleDelete}
                      >
                        삭제
                      </Button>
                    )}
                    <Button
                      type="submit"
                      variant="primary"
                      size="large"
                      fullWidth
                    >
                      저장
                    </Button>
                  </ModalActions>
                </ModalForm>
              </>
            )}
          </ModalContent>
        </ModalOverlay>

        {dialog && (
          <DialogOverlay onClick={() => dialog.type === 'confirm' && closeDialog(false)}>
            <DialogBox onClick={(e) => e.stopPropagation()}>
              <DialogMessage>{dialog.message}</DialogMessage>
              <DialogActions>
                {dialog.type === 'confirm' ? (
                  <>
                    <Button variant="outline" fullWidth onClick={() => closeDialog(false)}>
                      취소
                    </Button>
                    <Button variant="primary" fullWidth onClick={() => closeDialog(true)}>
                      확인
                    </Button>
                  </>
                ) : (
                  <Button variant="primary" fullWidth onClick={() => closeDialog(true)}>
                    확인
                  </Button>
                )}
              </DialogActions>
            </DialogBox>
          </DialogOverlay>
        )}
      </UniformsContainer>
    </DashboardLayout>
  );
};

export default AdminUniforms;

